import { apiClient } from './client'
import type { Quote, QuoteImage, Source } from '../types'

export type ReviewKind = 'quote' | 'source'

export interface ReviewCategory {
  key: string
  kind: ReviewKind
  label: string
  hint: string
  count: number
}

export type ReviewQuote = Quote & { missing: string[] }
export type ReviewSource = Source & { missing: string[] }

export interface ReviewItemsResponse {
  items: (ReviewQuote | ReviewSource)[]
  total: number
  page: number
  page_size: number
  kind: ReviewKind
}

export interface QuoteUpdate {
  source_id: number
  text_content: string
  text_status: 'ai_generated' | 'manual_verified'
  tag_names: string[]
}

export interface SourceUpdate {
  type: 'book' | 'article'
  title: string
  isbn?: string | null
  doi?: string | null
  issn?: string | null
  publisher?: string | null
  year?: number | null
  author_ids: number[]
}

export const reviewApi = {
  getStats: async (): Promise<{ categories: ReviewCategory[] }> => {
    const response = await apiClient.get('/review/stats')
    return response.data
  },

  getItems: async (category: string, page = 1, pageSize = 20): Promise<ReviewItemsResponse> => {
    const response = await apiClient.get('/review/items', {
      params: { category, page, page_size: pageSize },
    })
    return response.data
  },

  // PUT /quotes/{id} erwartet Formularfelder, kein JSON
  updateQuote: async (id: number, update: QuoteUpdate): Promise<Quote> => {
    const formData = new FormData()
    formData.append('source_id', String(update.source_id))
    formData.append('text_content', update.text_content)
    formData.append('text_status', update.text_status)
    formData.append('tag_names', update.tag_names.join(','))

    const response = await apiClient.put(`/quotes/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  // Der Endpunkt nimmt genau ein Bild pro Request entgegen (Feldname 'image')
  addImage: async (quoteId: number, file: File): Promise<Quote> => {
    const formData = new FormData()
    formData.append('image', file)

    const response = await apiClient.post(`/quotes/${quoteId}/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  updateImagePage: async (
    quoteId: number,
    imageId: number,
    pageNumber: string
  ): Promise<QuoteImage> => {
    const formData = new FormData()
    formData.append('page_number', pageNumber)

    const response = await apiClient.patch(
      `/quotes/${quoteId}/images/${imageId}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
    return response.data
  },

  updateSource: async (id: number, update: SourceUpdate): Promise<Source> => {
    const response = await apiClient.put(`/sources/${id}`, update)
    return response.data
  },
}
